// v10/src/components/admin/inventory/modals/SerializedItemModal.js
//
// Modal "Gestionar S/N" del Stock de repuestos (A8) — Tarea 2.
// ABM de items con número de serie con transiciones de status.
//
// Endpoints:
//   POST   /api/inventory-items/:itemId/serialized
//   PUT    /api/inventory-serialized/:id
//   DELETE /api/inventory-serialized/:id

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, Hash, Save, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import inventoryService from '../../../../services/inventoryService';

const STATUSES = ['in_stock', 'installed', 'retired'];

const todayIso = () => new Date().toISOString().split('T')[0];

const toDateInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDateAR = (value) => {
  const d = toDateInput(value);
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

const SerializedItemModal = ({
  isOpen,
  mode = 'create',
  item,
  serializedItem,
  aircraft = [],
  onClose,
  onSuccess,
  darkMode,
}) => {
  const { t } = useTranslation('admin');

  const isEdit = mode === 'edit' && !!serializedItem;
  const initialStatus = isEdit ? (serializedItem.status || 'in_stock') : 'in_stock';

  const [serial, setSerial] = useState('');
  const [status, setStatus] = useState(initialStatus);
  const [aircraftId, setAircraftId] = useState('');
  const [installDate, setInstallDate] = useState('');
  const [horometer, setHorometer] = useState('');
  const [retireDate, setRetireDate] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  // Reset al abrir
  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setSerial(serializedItem.serial_number || '');
      setStatus(serializedItem.status || 'in_stock');
      setAircraftId(serializedItem.installed_at_aircraft_id || '');
      setInstallDate(toDateInput(serializedItem.installed_at_date));
      setHorometer(
        serializedItem.installed_at_horometer != null
          ? String(serializedItem.installed_at_horometer)
          : '',
      );
      setRetireDate(toDateInput(serializedItem.retired_at_date));
      setNotes(serializedItem.notes || '');
    } else {
      setSerial('');
      setStatus('in_stock');
      setAircraftId('');
      setInstallDate(todayIso());
      setHorometer('');
      setRetireDate(todayIso());
      setNotes('');
    }
    setError('');
    setConfirmDelete(false);
  }, [isOpen, isEdit, serializedItem]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting && !deleting) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, submitting, deleting]);

  const isRetiredOrigin = isEdit && serializedItem?.status === 'retired';

  const validation = useMemo(() => {
    const errors = [];
    if (!isEdit && !serial.trim()) {
      errors.push(t('inventory_modal_serialized_validation_serial_required'));
    }
    if (status === 'installed') {
      if (!aircraftId || !installDate || horometer === '') {
        errors.push(t('inventory_modal_serialized_validation_install_required'));
      }
      if (horometer !== '') {
        const n = Number(horometer);
        if (!Number.isFinite(n) || n < 0) {
          errors.push(t('inventory_modal_serialized_validation_horometer_negative'));
        }
      }
    }
    if (status === 'retired') {
      if (!retireDate) errors.push(t('inventory_modal_serialized_validation_retire_required'));
    }
    if (isEdit) {
      if (isRetiredOrigin && status !== serializedItem.status) {
        errors.push(t('inventory_modal_serialized_validation_transition_invalid'));
      }
      // Transiciones permitidas (defensa cliente — backend valida igual):
      //  in_stock  → installed  ✅
      //  in_stock  → retired    ✅
      //  installed → in_stock   ✅
      //  installed → retired    ✅
      //  retired   → (ninguna)
      const from = serializedItem.status;
      const to = status;
      if (from && to && from !== to) {
        const allowed =
          (from === 'in_stock' && to === 'installed') ||
          (from === 'in_stock' && to === 'retired') ||
          (from === 'installed' && to === 'in_stock') ||
          (from === 'installed' && to === 'retired');
        if (!allowed && !isRetiredOrigin) {
          errors.push(t('inventory_modal_serialized_validation_status_invalid'));
        }
      }
    }
    return { errors, ok: errors.length === 0 };
  }, [serial, status, aircraftId, installDate, horometer, retireDate, isEdit, isRetiredOrigin, serializedItem, t]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validation.ok || submitting || !item?.id) return;

    setSubmitting(true);
    setError('');

    try {
      if (!isEdit) {
        const payload = {
          serial_number: serial.trim(),
          status,
          notes: notes.trim() || null,
        };
        if (status === 'installed') {
          payload.installed_at_aircraft_id = aircraftId;
          payload.installed_at_date = installDate;
          payload.installed_at_horometer = Number(horometer);
        }
        if (status === 'retired') {
          payload.retired_at_date = retireDate;
        }
        await inventoryService.createSerialized(item.id, payload);
      } else {
        const payload = {
          status,
          notes: notes.trim() || null,
        };
        if (status === 'installed') {
          payload.installed_at_aircraft_id = aircraftId;
          payload.installed_at_date = installDate;
          payload.installed_at_horometer = Number(horometer);
        } else if (status === 'in_stock') {
          payload.installed_at_aircraft_id = null;
          payload.installed_at_date = null;
          payload.installed_at_horometer = null;
        } else if (status === 'retired') {
          payload.retired_at_date = retireDate;
        }
        await inventoryService.updateSerialized(serializedItem.id, payload);
      }
      if (!isMounted.current) return;
      onSuccess?.();
      onClose?.();
    } catch (err) {
      if (!isMounted.current) return;
      setError(err?.response?.data?.message || t('inventory_modal_network_error'));
    } finally {
      if (isMounted.current) setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!serializedItem?.id || deleting) return;
    setDeleting(true);
    setError('');
    try {
      await inventoryService.deleteSerialized(serializedItem.id);
      if (!isMounted.current) return;
      onSuccess?.();
      onClose?.();
    } catch (err) {
      if (!isMounted.current) return;
      setError(err?.response?.data?.message || t('inventory_modal_network_error'));
    } finally {
      if (isMounted.current) setDeleting(false);
    }
  };

  if (!isOpen || !item) return null;

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const labelStyle = {
    margin: '0 0 6px', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: fgFaint,
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
    color: fgPrimary, fontSize: 14, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };
  const lockedStyle = { ...inputStyle, opacity: 0.55, cursor: 'not-allowed', background: darkMode ? 'rgba(255,255,255,0.02)' : '#f1f5f9' };

  const statusOptions = STATUSES.map((s) => ({
    key: s,
    label: t(`inventory_modal_serialized_status_${s}`),
  }));

  const canDelete = isEdit && serializedItem?.status === 'in_stock';
  const showRetiredInfo = isEdit && status === 'retired' && (serializedItem.aircraft_registration || serializedItem.installed_at_aircraft_id);

  const transitionWarning = useMemo(() => {
    if (!isEdit) return null;
    const from = serializedItem?.status;
    const to = status;
    if (!from || from === to) return null;
    if (to === 'installed') return t('inventory_modal_serialized_status_change_warning_to_installed');
    if (to === 'retired')   return t('inventory_modal_serialized_status_change_warning_to_retired');
    if (to === 'in_stock')  return t('inventory_modal_serialized_status_change_warning_to_in_stock');
    return null;
  }, [isEdit, serializedItem?.status, status, t]);

  const titleKey = isEdit ? 'inventory_modal_serialized_edit_title' : 'inventory_modal_serialized_create_title';
  const subtitleKey = isEdit ? 'inventory_modal_serialized_subtitle_edit' : 'inventory_modal_serialized_subtitle_create';
  const subtitle = isEdit
    ? t(subtitleKey, { serial: serializedItem?.serial_number, name: item.name })
    : t(subtitleKey, { name: item.name });

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 110,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={submitting || deleting ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 600, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'linear-gradient(135deg,#0d9488,#14b8a6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 16px rgba(13,148,136,0.3)',
            }}>
              <Hash style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {t(titleKey)}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted, maxWidth: 380 }}>
                {subtitle}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose} disabled={submitting || deleting}
            aria-label={t('inventory_modal_close')}
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent',
              cursor: (submitting || deleting) ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Serial */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('inventory_modal_serialized_serial_label')}</p>
            <input
              type="text" maxLength={120}
              style={isEdit ? lockedStyle : inputStyle}
              value={serial}
              onChange={(e) => !isEdit && setSerial(e.target.value)}
              disabled={isEdit}
              readOnly={isEdit}
              placeholder={t('inventory_modal_serialized_serial_placeholder')}
            />
          </div>

          {/* Status */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('inventory_modal_serialized_status_label')}</p>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={isRetiredOrigin}
              style={{
                ...(isRetiredOrigin ? lockedStyle : inputStyle),
                cursor: isRetiredOrigin ? 'not-allowed' : 'pointer',
                appearance: 'auto',
              }}
            >
              {statusOptions.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            {isRetiredOrigin && (
              <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint }}>
                {t('inventory_modal_serialized_validation_transition_invalid')}
              </p>
            )}
            {transitionWarning && (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#0d9488', fontWeight: 600 }}>
                {transitionWarning}
              </p>
            )}
          </div>

          {/* Installed fields */}
          {status === 'installed' && (
            <>
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('inventory_modal_serialized_aircraft_label')}</p>
                <select
                  value={aircraftId}
                  onChange={(e) => setAircraftId(e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
                >
                  <option value="">{t('inventory_modal_serialized_aircraft_placeholder')}</option>
                  {aircraft.map((ac) => (
                    <option key={ac.id} value={ac.id}>
                      {ac.registration} — {ac.manufacturer ? `${ac.manufacturer} ` : ''}{ac.model_name || ''}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <p style={labelStyle}>{t('inventory_modal_serialized_install_date_label')}</p>
                  <input
                    type="date" max={todayIso()}
                    style={{ ...inputStyle, cursor: 'text' }}
                    value={installDate}
                    onChange={(e) => setInstallDate(e.target.value)}
                  />
                </div>
                <div>
                  <p style={labelStyle}>{t('inventory_modal_serialized_horometer_label')}</p>
                  <input
                    type="number" min="0" step="0.01"
                    style={inputStyle}
                    value={horometer}
                    onChange={(e) => setHorometer(e.target.value)}
                    placeholder={t('inventory_modal_serialized_horometer_placeholder')}
                  />
                </div>
              </div>
            </>
          )}

          {/* Retired field */}
          {status === 'retired' && (
            <>
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('inventory_modal_serialized_retire_date_label')}</p>
                <input
                  type="date" max={todayIso()}
                  style={{ ...inputStyle, cursor: 'text' }}
                  value={retireDate}
                  onChange={(e) => setRetireDate(e.target.value)}
                  disabled={isRetiredOrigin}
                />
              </div>
              {showRetiredInfo && (
                <p style={{ margin: '0 0 14px', fontSize: 12, color: fgMuted }}>
                  {t('inventory_modal_serialized_retired_info', {
                    registration: serializedItem.aircraft_registration || '—',
                    date: formatDateAR(serializedItem.retired_at_date) || '—',
                  })}
                </p>
              )}
            </>
          )}

          {/* Notes */}
          <div style={{ marginBottom: 16 }}>
            <p style={labelStyle}>{t('inventory_modal_serialized_notes_label')}</p>
            <textarea
              rows={2} style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('inventory_modal_serialized_notes_placeholder')}
            />
          </div>

          {/* Validation errors */}
          {validation.errors.length > 0 && (
            <div style={{
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
              marginBottom: 12,
            }}>
              {validation.errors.map((err, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '4px 0 0', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                  • {err}
                </p>
              ))}
            </div>
          )}

          {error && (
            <div style={{
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
              marginBottom: 12,
            }}>
              <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
            </div>
          )}

          {/* Confirmación de eliminación inline */}
          {canDelete && confirmDelete && (
            <div style={{
              padding: 14, borderRadius: 12, marginBottom: 12,
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
            }}>
              <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#dc2626' }}>
                {t('inventory_modal_serialized_delete_inline_confirm')}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <motion.button
                  type="button" onClick={handleDelete} disabled={deleting}
                  whileHover={!deleting ? { scale: 1.02 } : {}}
                  whileTap={!deleting ? { scale: 0.97 } : {}}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 14px', borderRadius: 10, border: 'none',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    background: 'linear-gradient(135deg,#dc2626,#ef4444)',
                    color: 'white', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                  }}
                >
                  <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
                  {deleting
                    ? <Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                    : <Trash2 style={{ width: 14, height: 14 }} />
                  }
                  {t('inventory_modal_serialized_delete_action')}
                </motion.button>
                <button
                  type="button" onClick={() => setConfirmDelete(false)} disabled={deleting}
                  style={{
                    padding: '8px 14px', borderRadius: 10,
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                    background: 'transparent',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    fontWeight: 600, fontSize: 13, color: fgMuted, fontFamily: 'inherit',
                  }}
                >
                  {t('inventory_modal_btn_cancel')}
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
            <motion.button
              type="submit" disabled={submitting || !validation.ok}
              whileHover={!submitting && validation.ok ? { scale: 1.02 } : {}}
              whileTap={!submitting && validation.ok ? { scale: 0.97 } : {}}
              style={{
                flex: 1, minWidth: 200, padding: 13, borderRadius: 14, border: 'none',
                cursor: submitting || !validation.ok ? 'not-allowed' : 'pointer',
                background: submitting || !validation.ok
                  ? (darkMode ? 'rgba(255,255,255,0.08)' : '#cbd5e1')
                  : 'linear-gradient(135deg,#0d9488,#14b8a6)',
                color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              {submitting
                ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : (isEdit
                    ? <Save style={{ width: 16, height: 16 }} />
                    : <Plus style={{ width: 16, height: 16 }} />)
              }
              {isEdit
                ? (submitting ? t('inventory_modal_btn_saving') : t('inventory_modal_serialized_action_save'))
                : (submitting ? t('inventory_modal_btn_creating') : t('inventory_modal_serialized_action_create'))}
            </motion.button>
            {canDelete && !confirmDelete && (
              <button
                type="button" onClick={() => setConfirmDelete(true)} disabled={submitting || deleting}
                style={{
                  padding: '13px 18px', borderRadius: 14, border: 'none',
                  background: 'transparent', color: '#ef4444',
                  fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                  cursor: (submitting || deleting) ? 'not-allowed' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}
              >
                <Trash2 style={{ width: 16, height: 16 }} />
                {t('inventory_modal_serialized_action_delete')}
              </button>
            )}
            <button
              type="button" onClick={onClose} disabled={submitting || deleting}
              style={{
                padding: '13px 18px', borderRadius: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: 'transparent',
                cursor: (submitting || deleting) ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14, color: fgMuted, fontFamily: 'inherit',
              }}
            >
              {t('inventory_modal_btn_cancel')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default SerializedItemModal;
